import { showDeleteConfirmation, showConfirmation } from '@modules/common';
import { switchMap, tap, map } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { Session } from '@modules/auth/session';
import { R } from '@core/utils/r';
import { ARDashboardResponse, ARBusinessViewResponse, ARVizpadResponse } from '@business/entities/advancedReporting';
import { AdvancedReportingAPI } from '@business/api/advanced_reporting';

export class ARDashboardBloc extends BaseBloc {
  private readonly dashboardRepo = new Repository<ARDashboardResponse>({
    getItemId: item => item.id,
  });

  private readonly businessViewRepo = new Repository<ARBusinessViewResponse>({
    getItemId: item => item.id,
  });

  private readonly vizpadRepo = new Repository<ARVizpadResponse>({
    getItemId: item => item.id,
  });

  readonly businessViews$ = this.businessViewRepo.items$;
  readonly vizpads$ = this.vizpadRepo.items$;

  readonly dashboards$ = this.dashboardRepo.items$.pipe(map(items => R.sortBy(items, 'title')));
  selectDashboard = this.dashboardRepo.selectItem;

  onReset() {
    this.dashboardRepo.reset();
  }

  fetchBusinessViews = () => {
    return AdvancedReportingAPI.fetchBusinessViews().pipe(
      this.businessViewRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  fetchVizpads = () => {
    return AdvancedReportingAPI.fetchVizpads().pipe(this.vizpadRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_general_error') }));
  };

  fetchDashoards = () => {
    return AdvancedReportingAPI.fetchDashoards().pipe(
      this.dashboardRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  fetchDashboard = (id: string) => {
    return AdvancedReportingAPI.fetchDashboard(id).pipe(
      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  createDashboard = (data: Partial<ARDashboardResponse>) => {
    return AdvancedReportingAPI.createDashboard(data).pipe(
      this.dashboardRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_dashboard'),
      }),
    );
  };

  updateDashboard = (id: string, data: Partial<ARDashboardResponse>) => {
    return AdvancedReportingAPI.updateDashboard(id, data).pipe(
      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_dashboard'),
      }),
    );
  };

  deleteDashboard = (id: string) => {
    return showDeleteConfirmation('Delete Dashboard', 'Do you really want to remove this dashboard?').pipe(
      switchMap(() => AdvancedReportingAPI.deleteDashboard(id)),

      this.dashboardRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_dashboard'),
      }),
    );
  };

  copyDashboard = (id: string) => {
    return showConfirmation('Copy Dashboard', 'Do you really want to copy this dashboard?').pipe(
      switchMap(() => AdvancedReportingAPI.copyDashboard(id)),

      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_dashboard'),
      }),
    );
  };
}
